import { useState } from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    id: "getting-started",
    emoji: "🚀",
    title: "Getting Started",
    articles: [
      {
        title: "What is Digitoly QR?",
        content: `Digitoly QR is a smart QR code platform that goes far beyond basic redirects. Every QR code you create can capture leads, collect reviews, display menus, show coupons, connect to WiFi, and provide deep analytics — all from one dashboard.

Your free 7-day trial gives you full Agency-level access to every feature. No credit card required to start.`
      },
      {
        title: "Creating your first QR code",
        content: `1. Click "Create New" in the sidebar
2. Choose a QR Mode (Direct, Leads, Feedback, WhatsApp, WiFi, UPI, Menu)
3. Fill in the required fields for your chosen mode
4. Customize the design in the Style tab (colors, dots, eyes)
5. Add your logo in the Designer tab
6. Click "Save Asset"
7. Download your QR as PNG, JPG, SVG, or PDF

Your QR code is now live and trackable. Any scan is recorded in real-time.`
      },
      {
        title: "Dynamic vs Static QR codes",
        content: `Dynamic QR codes (recommended): The destination can be changed anytime without reprinting. Every scan is tracked with location, device, time, and browser data.

Static QR codes: The destination is permanent and encoded directly into the QR image. Cannot be tracked or changed after printing.

For any business use, always choose Dynamic.`
      },
    ]
  },
  {
    id: "qr-modes",
    emoji: "📱",
    title: "QR Code Modes",
    articles: [
      {
        title: "Direct URL QR",
        content: `The simplest mode. Scanning takes the user directly to your website, product page, social media, or any URL.

Best for: Website links, product pages, social media profiles, event registrations.

Setup: Enter the destination URL and save. Optionally enable A/B testing to split traffic between two URLs, or set an expiry date/max scans limit.`
      },
      {
        title: "Leads QR — Capture customer details",
        content: `When scanned, shows a form asking for Name, Email, and Phone number. After submitting, the customer is redirected to your destination URL.

All leads are saved in your Leads Inbox. You can export them as CSV.

Enable scan notifications to get an email alert every time a lead is submitted.

Best for: Events, giveaways, loyalty programs, exclusive content access, product launches.`
      },
      {
        title: "Feedback QR — Smart review filter",
        content: `A reputation management tool. When scanned, customers rate you 1-5 stars.

4-5 stars: Customer is instantly sent to your Google review page to leave a public review.
1-3 stars: Customer is shown a private feedback form. You get notified and can resolve the issue before it becomes a public negative review.

Best for: Restaurants, hotels, salons, retail stores, any customer-facing business.

Setup: Enter your Google review page URL as the destination URL.`
      },
      {
        title: "WhatsApp QR",
        content: `Scanning opens WhatsApp with a pre-filled message to your number. The user just taps Send.

Setup: 
1. Select WhatsApp mode
2. Enter your WhatsApp number with country code (auto-detected via the flag selector)
3. Optionally enter a pre-filled message (e.g. "Hi, I'd like to place an order")
4. Save and download

Best for: Customer support, order taking, inquiries, appointment booking.`
      },
      {
        title: "WiFi QR",
        content: `Scanning automatically connects the phone to your WiFi network — no typing required.

Setup:
1. Select WiFi mode
2. Enter Network Name (SSID)
3. Enter Password
4. Select security type (WPA/WPA2 for most modern routers)
5. Toggle "Hidden Network" if your WiFi is hidden

Important: WiFi QR codes must be scanned with a camera app, not opened as a URL.

Best for: Cafes, hotels, offices, events, co-working spaces.`
      },
      {
        title: "UPI Payment QR",
        content: `Scanning opens any UPI payment app (PhonePe, GPay, Paytm, etc.) with your payment details pre-filled.

Setup:
1. Select UPI mode
2. Enter your UPI ID (e.g. yourname@paytm or 9999999999@upi)
3. Enter display name (your business name)
4. Optionally enter a fixed amount

Important: UPI QR codes must be scanned with a camera or UPI app directly. They cannot be opened as a URL in a browser.

Best for: Small businesses, street vendors, freelancers, shops accepting UPI payments.`
      },
      {
        title: "Smart Menu QR",
        content: `Creates a beautiful digital menu that customers see when they scan. Fully mobile-optimized with categories, item descriptions, prices, veg/non-veg indicators, and photos.

Setup:
1. Create a QR code in Menu mode and save it
2. Go to Menu Builder in the sidebar
3. Select your QR code
4. Click "Create Menu"
5. Add categories (Starters, Main Course, etc.)
6. Add items with name, description, price, photo, and veg/non-veg tag
7. Toggle items as Available/Unavailable in real-time

Best for: Restaurants, cafes, food trucks, catering businesses.`
      },
      {
        title: "vCard QR — Digital business card",
        content: `Scanning saves your contact details directly to the phone's contacts app. No app required.

Setup: Fill in First Name, Last Name, Phone, Email, Address, and Website.

Best for: Business cards, name badges, networking events, conference booths.`
      },
    ]
  },
  {
    id: "advanced-features",
    emoji: "⚡",
    title: "Advanced Features",
    articles: [
      {
        title: "PIN Protection",
        content: `Add a PIN code to any QR so only authorized people can access the content.

When scanned, users see a PIN entry screen. Only those with the correct PIN proceed.

Setup: In the Advanced tab, enter a PIN (up to 8 digits) in the PIN Protection field.

Best for: Exclusive content, staff-only resources, event VIP access, confidential documents.`
      },
      {
        title: "A/B Split Testing",
        content: `Test two different destination URLs against each other. Traffic is split 50/50 randomly between URL A and URL B.

View results in the QR's Analytics page — it shows which version got more scans and declares a winner.

Setup: In the Advanced tab, toggle A/B Testing ON and enter both URLs.

Note: A/B testing is only available for Direct mode QR codes.`
      },
      {
        title: "QR Expiry — Date and scan limits",
        content: `Automatically deactivate a QR after a specific date or after a maximum number of scans.

Date expiry: QR stops working after midnight on the selected date.
Scan limit: QR stops working after N scans. Perfect for limited offers.

Setup: In the Advanced tab, set Expire After Date and/or Max Scans.

When expired, scanners see an "⏰ QR Code Expired" message.

Best for: Time-limited promotions, event tickets, limited offers, seasonal campaigns.`
      },
      {
        title: "Retargeting Pixels",
        content: `Add your Meta (Facebook) or Google Pixel ID to retarget everyone who scans your QR code with ads.

Setup: In the Advanced tab, enter your Pixel ID(s).
- Meta Pixel: Find in Facebook Business Manager → Events Manager
- Google Tag: Find in Google Ads → Tools → Tag Manager

Once added, every scan fires the pixel — allowing you to show ads to people who scanned your QR.

Best for: E-commerce, event marketing, product launches, any business running digital ads.`
      },
      {
        title: "Dynamic Coupons",
        content: `Each scan shows a unique coupon code from your list. Prevents coupon sharing and lets you track redemption.

Setup:
1. Create a Direct mode QR and enable Coupon Mode in the Advanced tab
2. Save the QR
3. Go to Coupons in the sidebar
4. Select your QR
5. Upload your coupon codes (one per line or CSV)

When scanned, each customer gets one unique code. Once a code is used, it won't appear again.

Best for: Retail promotions, app downloads, loyalty rewards, product launches.`
      },
      {
        title: "Time-Based Routing",
        content: `Redirect to different URLs at different times of day. The QR automatically switches based on your schedule.

Example: 
- 9am-5pm → Your office booking page
- 5pm-9pm → Your after-hours message
- Closed hours → "We're closed" fallback page

Setup: In the Content tab, enable Time Routing and add time slots with URLs.

Best for: Restaurants (lunch/dinner menus), businesses with varying hours, events with multiple sessions.`
      },
    ]
  },
  {
    id: "analytics",
    emoji: "📊",
    title: "Analytics",
    articles: [
      {
        title: "Per-QR Analytics",
        content: `Click Analytics on any QR card to see detailed stats:

Overview tab: Total scans, unique visitors, repeat scans, conversion rate (for Lead QRs), scan timeline chart.

Geography tab: Top countries and cities with progress bars and bar chart.

Audience tab: Device types (Mobile vs Desktop) as pie chart, browser breakdown.

Heatmap tab: Which hours of the day get the most scans. Shows peak hour.

Recent Scans tab: Live table of last 20 scans with time, country, city, device, browser, and IP address.

A/B Results tab: Appears only for QRs with A/B testing. Shows winner, scan counts, and percentages.`
      },
      {
        title: "Powerhouse — Global Analytics",
        content: `The Powerhouse dashboard shows combined analytics across ALL your QR codes.

- Growth Velocity: Total scan trend over time
- Device Mix: Mobile vs Desktop breakdown
- Campaign Leaderboard: Your top 5 performing QRs
- Peak Scan Hours: When all your QRs get the most scans combined

Access via Analytics → Powerhouse in the sidebar.`
      },
      {
        title: "QR Health Monitor",
        content: `Checks if your destination URLs are still working. Detects dead links (404 errors, unreachable sites) before they cost you customers.

Run a health check anytime from Health Monitor in the sidebar.

Results show Healthy (green) or Dead Link (red) for each QR. Export results or fix broken links via the Edit button.

Schedule: Run this check monthly or before important campaigns.`
      },
    ]
  },
  {
    id: "organization",
    emoji: "📁",
    title: "Organization",
    articles: [
      {
        title: "Folders — Organize your QR codes",
        content: `Group QR codes into color-coded folders for easy management.

Create a folder: Click "+ New Folder" on My Assets, pick a color and name.
Move a QR: Click the 📁 Move button on any QR card and select a folder.
Filter: Click a folder name to see only QRs in that folder.
Delete: Hover over a folder tag and click ×.

Best practice: Create folders by client, campaign, or location (e.g. "Mumbai Store", "Summer 2026", "Client — ABC Corp").`
      },
      {
        title: "Search",
        content: `Use the search bar on My Assets to filter QR codes by name in real-time. Works across all folders simultaneously.`
      },
      {
        title: "Duplicate a QR code",
        content: `Click the Duplicate button on any QR card to create an identical copy with a new unique code.

The copy gets "(Copy)" added to its name. All settings, design, and mode are preserved.

Best for: Creating slight variations of a campaign, testing different designs, setting up QRs for multiple locations.`
      },
    ]
  },
  {
    id: "team",
    emoji: "👥",
    title: "Team & Collaboration",
    articles: [
      {
        title: "Inviting team members",
        content: `Go to Team in the sidebar to invite colleagues.

Viewer role: Can see analytics only. Cannot create or edit QRs.
Editor role: Can create and edit QR codes under your account.

Enter their email and select a role. They must sign up with the same email to activate access.

Available on Pro and Agency plans.`
      },
    ]
  },
  {
    id: "billing",
    emoji: "💳",
    title: "Plans & Billing",
    articles: [
      {
        title: "Free Trial",
        content: `Every new account gets 7 days of full Agency-level access for free. No credit card required.

During your trial you can:
- Create up to 50 QR codes
- Use all modes (Leads, Feedback, WiFi, UPI, Menu, etc.)
- Access all analytics
- Use bulk generation
- Invite team members

After 7 days, your account moves to the Free plan (5 QR codes, direct mode only) unless you upgrade.`
      },
      {
        title: "Plans comparison",
        content: `Free: 5 QR codes · Direct mode only · No analytics · No leads/feedback

Pro (₹499/mo or ₹4999/yr): 50 QR codes · All modes · Full analytics · Leads & Feedback · WiFi, UPI, Menu · No bulk generation

Agency (₹1499/mo or ₹14999/yr): Unlimited QR codes · Everything in Pro · Bulk QR generation · Team members · Priority support

Save 17% by choosing yearly billing.`
      },
      {
        title: "Cancellation & Refunds",
        content: `You can cancel anytime from your Razorpay subscription dashboard. Access continues until the end of your billing period.

For refund requests, contact support@digitoly.com within 7 days of payment.`
      },
    ]
  },
];

export default function HelpCenter() {
  const [activeSection, setActiveSection] = useState("getting-started");
  const [activeArticle, setActiveArticle] = useState(0);
  const [search, setSearch] = useState("");

  const filteredSections = search
    ? SECTIONS.map(s => ({
        ...s,
        articles: s.articles.filter(a =>
          a.title.toLowerCase().includes(search.toLowerCase()) ||
          a.content.toLowerCase().includes(search.toLowerCase())
        )
      })).filter(s => s.articles.length > 0)
    : SECTIONS;

  const currentSection = SECTIONS.find(s => s.id === activeSection);
  const currentArticle = currentSection?.articles[activeArticle];

  return (
    <div className="min-h-screen bg-[#020617] text-white">
      {/* Header */}
      <div className="border-b border-white/10 px-8 py-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black uppercase italic tracking-tighter">Help Center</h1>
            <p className="text-gray-500 text-[10px] uppercase tracking-widest mt-1">Complete guide to Digitoly QR</p>
          </div>
          <Link to="/dashboard" className="text-[10px] font-black uppercase text-gray-500 hover:text-white transition-all">
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-8 py-8">
        {/* Search */}
        <input
          type="text"
          placeholder="Search articles..."
          value={search}
          onChange={e => { setSearch(e.target.value); }}
          className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white text-sm outline-none focus:border-blue-500 mb-8 transition-all"
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="space-y-2">
            {filteredSections.map(section => (
              <div key={section.id}>
                <button
                  onClick={() => { setActiveSection(section.id); setActiveArticle(0); setSearch(""); }}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all flex items-center gap-3 ${activeSection === section.id && !search ? "bg-blue-600 text-white" : "bg-white/5 text-gray-400 hover:bg-white/10"}`}
                >
                  <span>{section.emoji}</span>
                  <span className="text-[11px] font-black uppercase tracking-wide">{section.title}</span>
                </button>
                {(activeSection === section.id || search) && section.articles.map((article, i) => (
                  <button
                    key={i}
                    onClick={() => { setActiveSection(section.id); setActiveArticle(i); setSearch(""); }}
                    className={`w-full text-left px-4 py-2 ml-4 rounded-xl transition-all text-[10px] font-black uppercase tracking-wide ${activeSection === section.id && activeArticle === i && !search ? "text-blue-400" : "text-gray-600 hover:text-gray-400"}`}
                  >
                    {article.title}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {search ? (
              <div className="space-y-4">
                {filteredSections.flatMap(s => s.articles.map((a, i) => (
                  <button
                    key={`${s.id}-${i}`}
                    onClick={() => { setActiveSection(s.id); setActiveArticle(s.articles.indexOf(a) === -1 ? 0 : SECTIONS.find(sec => sec.id === s.id)?.articles.indexOf(a) || 0); setSearch(""); }}
                    className="w-full text-left bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-blue-500/50 transition-all"
                  >
                    <p className="text-[10px] text-blue-400 font-black uppercase mb-1">{s.emoji} {s.title}</p>
                    <p className="text-white font-black text-sm mb-2">{a.title}</p>
                    <p className="text-gray-500 text-[11px] line-clamp-2">{a.content.slice(0, 120)}...</p>
                  </button>
                )))}
              </div>
            ) : currentArticle ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] text-blue-400 font-black uppercase">{currentSection?.emoji} {currentSection?.title}</span>
                </div>
                <h2 className="text-2xl font-black text-white mb-6">{currentArticle.title}</h2>
                <div className="prose prose-invert max-w-none">
                  {currentArticle.content.split('\n\n').map((para, i) => (
                    <p key={i} className="text-gray-300 text-sm leading-relaxed mb-4 whitespace-pre-line">{para}</p>
                  ))}
                </div>

                {/* Navigation */}
                <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
                  <button
                    onClick={() => setActiveArticle(prev => Math.max(0, prev - 1))}
                    disabled={activeArticle === 0}
                    className="px-4 py-2 bg-white/5 rounded-xl text-[10px] font-black uppercase text-gray-400 disabled:opacity-30 hover:bg-white/10 transition-all"
                  >
                    ← Previous
                  </button>
                  <span className="text-[10px] text-gray-600 self-center">
                    {activeArticle + 1} / {currentSection?.articles.length}
                  </span>
                  <button
                    onClick={() => setActiveArticle(prev => Math.min((currentSection?.articles.length || 1) - 1, prev + 1))}
                    disabled={activeArticle === (currentSection?.articles.length || 1) - 1}
                    className="px-4 py-2 bg-white/5 rounded-xl text-[10px] font-black uppercase text-gray-400 disabled:opacity-30 hover:bg-white/10 transition-all"
                  >
                    Next →
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}